import {
  adminCasingVars,
  defaultCasingVars,
  deleteCasings,
  ecopetrolCasingVars,
  kocCasingVars,
  restoreCasings
} from "@/models/equipment/casings/predicates";
import type { Casings } from "gql/api";
import {
  Casings_Type_Enum,
  useCasingsQuery,
  useCasingsTypesQuery,
  useDeleteCasingsMutation,
  useInsertCasingMutation,
  useRestoreCasingsMutation,
  useUpdateCasingByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { casingTableColumns } from "./casingTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useCasings(
  isAdmin: boolean,
  includeTypes: Casings_Type_Enum[]
): UseEquipment<Casings> {
  const baseEquipment = useEquipment<Casings>(isAdmin);
  const title = "Casings";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocCasingVars;
    if (user.is_ecopetrol_customer) return ecopetrolCasingVars;
    if (user.is_admin) return adminCasingVars;
    return defaultCasingVars;
  };
  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useCasingsQuery({ variables: getVariables() });

  // Fetch casings_type enum data from database to populate dropdownOptions dynamically
  // Only fetch enum data for admin users since it's only needed for the admin casings table
  const { data: casingsTypesData, fetching: isLoadingCasingsTypes } =
    useCasingsTypesQuery({
      pause: !isAdmin
    });

  const allRows = computed(() => {
    return ((data.value?.casings as Casings[]) ?? []).filter(c =>
      includeTypes.includes(c.casing_type)
    );
  });

  const hashOrder: (keyof Casings)[] = ["imperial_name", "metric_name"];

  // Transform columns with dynamic dropdownOptions and format function from enum table data
  // Maps casings_type enum table (value, comment) to:
  // - dropdownOptions: {label: comment, value: value} for dropdown selection
  // - format function: value -> comment for display in table cells
  const columnsWithDynamicOptions = computed(() => {
    if (!isAdmin) {
      return casingTableColumns;
    }

    const casingsTypeEnum = casingsTypesData.value?.casings_type || [];

    const enumLookup = new Map(
      casingsTypeEnum.map(
        (item: { value: string; comment?: string | null }) => [
          item.value,
          item.comment || item.value
        ]
      )
    );

    const dropdownOptions = isLoadingCasingsTypes.value
      ? [{ label: "Loading...", value: "" }]
      : casingsTypeEnum.map(
          (item: { value: string; comment?: string | null }) => ({
            label: item.comment || item.value,
            value: item.value
          })
        );

    const formatFunction = (value: unknown) => {
      if (value == null) return "";
      const stringValue = String(value);
      return enumLookup.get(stringValue) || stringValue;
    };

    return casingTableColumns.map(column => {
      if (column.field === "casing_type" && column.isDropdown) {
        return {
          ...column,
          dropdownOptions,
          format: formatFunction
        };
      }
      return column;
    });
  });

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertCasing } = useInsertCasingMutation();
  const { executeMutation: executeUpdateCasing } =
    useUpdateCasingByPkMutation();
  const { executeMutation: executeDeleteCasing } = useDeleteCasingsMutation();
  const { executeMutation: executeRestoreCasing } = useRestoreCasingsMutation();

  const insertCasing = (casing: Omit<Casings, "sizing_import" | "user">) =>
    executeInsertCasing({ object: casing });

  const updateCasing = (id: string, casing: Casings) =>
    executeUpdateCasing({ id, object: casing });

  const deleteCasing = (ids: string[]) =>
    executeDeleteCasing({ where: deleteCasings(ids) });

  const restoreCasing = (ids: string[]) =>
    executeRestoreCasing({ where: restoreCasings(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "casings",
    id: v4(),
    casing_type: Casings_Type_Enum.Casing,
    imperial_name: "",
    metric_name: "",
    outer_diameter_inches: null,
    interior_diameter_inches: null,
    drift_inches: null,
    coupling_outer_diameter_inches: null,
    pound_per_foot: null,
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null
  }));

  return {
    searchPlaceholder: "Search Casings...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "4 1/2"`,
    columns: computed(() =>
      defineVisibleColumns(columnsWithDynamicOptions.value)
    ),
    handleUpsert: defineUpsert(insertCasing, updateCasing),
    handleDelete: defineDelete(deleteCasing),
    handleRestore: defineRestore(restoreCasing),
    totalCount: computed(() => data.value?.casings_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: casingTableColumns,
      globalSearchFields: [
        "metric_name",
        "imperial_name",
        "outer_diameter_inches"
      ],
      allRows,
      title
    })
  };
}
